/**
 * ЖИВЫЕ МАРКЕРЫ СТРОЕНИЙ на поле, постоянно анимированные, в гамме медальонов:
 *  - повреждение строения: медальон в углях, над ним живое пламя, дым и искры;
 *  - блокировка: красная печать со знаком block.png (тем же, что в правилах),
 *    вокруг медленно вращаются дуги;
 *  - шкала крепости: золотой шеврон, на нем стальные камни — сколько крепость еще
 *    выдержит — и горящие угли полученного урона.
 *
 * Рисует scenes/LiveFieldMarkers, позы считает utils/effects/liveMarkerPoses,
 * текстуры — utils/effects/liveMarkerTextures. Размеры — доли размера ячейки.
 */

export const LIVE_MARKER_TEXTURE_KEYS = {
  // Язык пламени (белый): цвет снизу вверх задают углы tint
  FLAME: 'live_marker_flame',
  // Медальоны — в своих цветах
  EMBER_PLATE: 'live_marker_ember_plate',
  BLOCK_PLATE: 'live_marker_block_plate',
  // Три дуги кольца печати (белые, красятся tint)
  BLOCK_ARCS: 'live_marker_block_arcs',
  // Шеврон шкалы крепости — в своих цветах
  FORTRESS_CHEVRON: 'live_marker_fortress_chevron',
};

// Знак блокировки из правил: public/img/icons/block.png, красится заливкой tint
export const BLOCK_SYMBOL_TEXTURE_KEY = 'block_icon';

// Язык пламени: низ светлее верха
export const FLAME_TINTS = {
  outerBottom: 0xffa51f,
  outerTop: 0xf0340e,
  innerBottom: 0xfff6b8,
  innerTop: 0xffc42e,
};

export const BUILDING_DAMAGE_MARKER = {
  plateSizeFactor: 0.72,
  halo: {sizeFactor: 1.215, tint: 0xff6a1f, minAlpha: 0.45, maxAlpha: 0.95, pulseMs: 800},
  flame: {sizeFactor: 0.468, innerShare: 0.6, baseYFactor: 0.207},
  embers: {count: 5, periodMs: 1300, riseFactor: 0.65, spreadFactor: 0.144, sizeFactor: 0.1, tint: 0xffb347},
  smoke: {count: 2, periodMs: 2300, riseFactor: 0.675, spreadFactor: 0.09, sizeFactor: 0.38, tint: 0x2c2622, alpha: 0.42},
};

export const BLOCKED_MARKER = {
  plateSizeFactor: 0.72,
  halo: {sizeFactor: 1.215, tint: 0xe2201c, minAlpha: 0.4, maxAlpha: 0.9, pulseMs: 1300},
  arcs: {sizeFactor: 0.846, tint: 0xff6a58, alpha: 0.95, periodMs: 5200},
  symbol: {
    sizeFactor: 0.342,
    tint: 0xfff0ea,
    glowTint: 0xe2201c,
    glowScale: 1.3,
    minAlpha: 0.78,
    pulseMs: 1300,
  },
};

// Какой значок у действия строительства на ячейке
export const CONSTRUCTION_MARKER_KINDS = {
  // Строение этого хода: уже потрачено действие
  BUILD: 'build',
  // Строение не проходит проверку
  INVALID: 'invalid',
  // Только запланировано: действие еще не потрачено
  PLAN: 'plan',
};

// Части значка: public/img/icons/action_*, вырезает buildConstructionMarkerArt.py
export const CONSTRUCTION_MARKER_TEXTURE_PATHS = {
  action_hand: '/img/icons/action_hand.png',
  action_hammer_part: '/img/icons/action_hammer_part.png',
  action_hourglass_part: '/img/icons/action_hourglass_part.png',
};

/**
 * Действие строительства: на медальоне рука держит молоток, и он бьет. На ударе знак
 * вздрагивает, вспышка и искры летят от бойка. Невалидное строение — красная печать
 * и частые беспокойные удары. У плана сталь и песочные часы, которые переворачиваются.
 * Точки в знаке — в кадре его картинки.
 */
export const CONSTRUCTION_MARKER = {
  plateSizeFactor: 0.92,
  symbolSizeFactor: 0.62,
  frameSize: 256,
  hand: {textureKey: 'action_hand', tint: 0xfff4dc, outlineTint: 0x2a1704, outlineScale: 1.09},
  hammer: {textureKey: 'action_hammer_part', pivot: {x: 110, y: 192}, impact: {x: 186, y: 146}},
  hourglass: {textureKey: 'action_hourglass_part', center: {x: 150, y: 152}},
  // Доля такта от удара до конца вспышки и искр
  impactShare: 0.35,
  // Все значки на поле мигают в одной фазе, от часов сцены: так сразу видно, где
  // были действия, а не когда они случились. Новый значок просто вливается в общий такт
  fade: {durationMs: 600, holdMs: 600, revealMs: 200},
  // Невалидное строение должно бросаться в глаза: печать крупнее и бьется как пульс
  invalidPulse: {minScale: 1.2, maxScale: 1.55, durationMs: 180, holdMs: 60},
  kinds: {
    [CONSTRUCTION_MARKER_KINDS.BUILD]: {
      plateKey: 'size_recruitment_plate',
      halo: {sizeFactor: 1.35, tint: 0xffb81f, minAlpha: 0.45, maxAlpha: 0.95, pulseMs: 1150},
      symbolTint: 0x4a2c08,
      // Половина такта мигания: удар приходит на середину полной видимости
      swingMs: 900,
      raiseAngle: -0.75,
      recoilAngle: 0.09,
      jolt: 0.07,
      sparks: {count: 7, spreadFactor: 0.36, sizeFactor: 0.17, tints: [0xfff2b0, 0xffa51f]},
      flash: {sizeFactor: 0.6, tint: 0xfff4c8},
    },
    [CONSTRUCTION_MARKER_KINDS.INVALID]: {
      plateKey: 'live_marker_block_plate',
      halo: {sizeFactor: 1.35, tint: 0xe2201c, minAlpha: 0.55, maxAlpha: 1, pulseMs: 560},
      arcs: {sizeFactor: 1.01, tint: 0xff6a58, alpha: 0.95, periodMs: 2600},
      symbolTint: 0xc00d0d,
      swingMs: 560,
      raiseAngle: -0.5,
      recoilAngle: 0.14,
      jolt: 0.1,
      sparks: {count: 6, spreadFactor: 0.34, sizeFactor: 0.16, tints: [0xffd2c0, 0xff4a2a]},
      flash: {sizeFactor: 0.6, tint: 0xff8a70},
    },
    [CONSTRUCTION_MARKER_KINDS.PLAN]: {
      plateKey: 'size_recruitment_plate',
      plateTint: 0xa9bedb,
      halo: {sizeFactor: 1.3, tint: 0x7fb2ff, minAlpha: 0.3, maxAlpha: 0.7, pulseMs: 1800},
      symbolTint: 0x1e3350,
      flipMs: 2400,
    },
  },
};

// Какой маркер у какого действия отряда
export const COMBAT_MARKER_KINDS = {
  MELEE: 'melee',
  SHOOTING: 'shooting',
};

/**
 * Отряд бьется или стреляет: золотой медальон с белым знаком действия. В ближнем
 * бою знак вздрагивает, а от него разлетаются искры ударов. При выстреле стрелы
 * света одна за другой срываются вперед, по направлению стрелы на знаке
 */
export const COMBAT_MARKER = {
  plateSizeFactor: 0.72,
  symbol: {sizeFactor: 0.396, tint: 0xfff6dc},
  kinds: {
    [COMBAT_MARKER_KINDS.MELEE]: {
      symbolKey: 'barracks_white',
      halo: {sizeFactor: 1.215, tint: 0xff4a1f, minAlpha: 0.5, maxAlpha: 1, pulseMs: 620},
      // Знак вздрагивает на ударе
      strikeMs: 620,
      strikeScale: 0.14,
      sparks: {count: 7, periodMs: 620, spreadFactor: 0.47, sizeFactor: 0.18, tints: [0xfff2b0, 0xff9a1f]},
    },
    [COMBAT_MARKER_KINDS.SHOOTING]: {
      symbolKey: 'shooting_white',
      halo: {sizeFactor: 1.215, tint: 0xffb81f, minAlpha: 0.45, maxAlpha: 0.95, pulseMs: 900},
      bolts: {count: 2, periodMs: 900, startFactor: 0.05, travelFactor: 0.6, sizeFactor: 0.27, tint: 0xfff2b0},
    },
  },
};

export const FORTRESS_GAUGE = {
  // Центр шеврона относительно центра ячейки и высота его картинки
  xFactor: -0.69,
  yFactor: 0.02,
  sizeFactor: 1.65,
  // Ломаная, по которой стоят камни: относительно центра шеврона
  apexXFactor: -0.175,
  armXFactor: 0.185,
  armYFactor: 0.58,
  glow: {sizeFactor: 1.5, tint: 0xffb81f, minAlpha: 0.2, maxAlpha: 0.5, pulseMs: 1500},
  intact: {
    sizeFactor: 0.165,
    outlineFactor: 1.3,
    tint: 0x8fa3c4,
    shineTint: 0xf2f7ff,
    // Блик бежит по камням сверху вниз
    shimmerMs: 2600,
    shimmerWidth: 0.16,
  },
  burning: {
    sizeFactor: 0.165,
    outlineFactor: 1.3,
    tint: 0x4a1405,
    glowTint: 0xff7a1f,
    glowSizeFactor: 0.42,
    flameSizeFactor: 0.21,
    flameYFactor: 0.07,
  },
  embers: {maxCount: 4, periodMs: 1400, riseFactor: 0.5, spreadFactor: 0.08, sizeFactor: 0.12, tint: 0xffb347},
};
